import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import type { EmailOtpType } from '@supabase/supabase-js'
import { Button } from '../components/Button'
import { Card } from '../components/Card'
import { ToastProvider, useToast } from '../components/Toast'
import { supabase } from '../lib/supabase'
import { useDb } from '../state/DbProvider'

function ResetInner() {
  const { actions } = useDb()
  const toast = useToast()
  const location = useLocation()
  const navigate = useNavigate()

  const [pw, setPw] = useState('')
  const [pw2, setPw2] = useState('')
  const [recoveryStatus, setRecoveryStatus] = useState<'preparing' | 'ready' | 'error'>('preparing')
  const [recoveryMessage, setRecoveryMessage] = useState('再設定リンクを確認しています…')

  useEffect(() => {
    let cancelled = false

    const prepareRecoverySession = async () => {
      const fail = (message: string) => {
        if (cancelled) return
        setRecoveryStatus('error')
        setRecoveryMessage(message)
      }

      const succeed = () => {
        if (cancelled) return
        setRecoveryStatus('ready')
        setRecoveryMessage('新しいパスワードを設定できます。')
      }

      try {
        const initialSession = await supabase.auth.getSession()
        if (initialSession.error) {
          fail(initialSession.error.message)
          return
        }

        if (initialSession.data.session) {
          window.history.replaceState({}, document.title, location.pathname)
          succeed()
          return
        }

        const searchParams = new URLSearchParams(window.location.search)
        const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''))
        const code = searchParams.get('code')
        const tokenHash = searchParams.get('token_hash')
        const type = searchParams.get('type')
        const accessToken = searchParams.get('access_token') ?? hashParams.get('access_token')
        const refreshToken = searchParams.get('refresh_token') ?? hashParams.get('refresh_token')

        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code)
          if (error) {
            fail(error.message)
            return
          }
        } else if (tokenHash && type) {
          const { error } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: type as EmailOtpType,
          })
          if (error) {
            fail(error.message)
            return
          }
        } else if (accessToken && refreshToken) {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          })
          if (error) {
            fail(error.message)
            return
          }
        }

        const { data, error } = await supabase.auth.getSession()
        if (error) {
          fail(error.message)
          return
        }

        if (!data.session) {
          fail('再設定リンクの認証情報を確認できませんでした。メールのリンクを開き直してください。')
          return
        }

        window.history.replaceState({}, document.title, location.pathname)
        succeed()
      } catch (error) {
        fail(error instanceof Error ? error.message : '再設定リンクの確認に失敗しました。')
      }
    }

    void prepareRecoverySession()

    return () => {
      cancelled = true
    }
  }, [location.pathname])

  return (
    <div className="mx-auto max-w-md">
      <Card title="新しいパスワードを設定" subtitle="メールの再設定リンクを確認後、パスワードを設定します">
        <div className="grid gap-3">
          <div
            className={
              recoveryStatus === 'error'
                ? 'rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700'
                : 'rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-600'
            }
          >
            {recoveryMessage}
          </div>

          <label className="grid gap-1 text-sm">
            <span className="text-slate-700">新しいパスワード</span>
            <input
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2"
              placeholder="8文字以上"
              type="password"
            />
          </label>

          <label className="grid gap-1 text-sm">
            <span className="text-slate-700">新しいパスワード（確認）</span>
            <input
              value={pw2}
              onChange={(e) => setPw2(e.target.value)}
              className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2"
              placeholder="同じものを入力"
              type="password"
            />
          </label>

          <Button
            variant="primary"
            disabled={recoveryStatus !== 'ready'}
            onClick={async () => {
              if (pw !== pw2) {
                toast.show('パスワードが一致しません', 'error')
                return
              }
              const res = await actions.resetPassword('', pw)
              if (!res.ok) {
                toast.show(res.message ?? '失敗しました', 'error')
                return
              }
              toast.show('パスワードを再設定しました。ログインしてください。', 'success')
              navigate('/login')
            }}
          >
            再設定
          </Button>

          <div className="text-sm">
            <Link to="/login" className="text-blue-600 hover:underline">
              ログインへ
            </Link>
          </div>
        </div>
      </Card>
    </div>
  )
}

export default function ResetPasswordPage() {
  return (
    <ToastProvider>
      <ResetInner />
    </ToastProvider>
  )
}
